/**
 * Community auth: login, signup, forgot-password, reset-password.
 * data-controller="AuthController", data-action="login" 등으로 디스패치.
 */
import { initAuthLoginForm } from "../components/AuthLoginForm";
import { initAuthSignupForm } from "../components/AuthSignupForm";
import { initAuthForgotPasswordForm } from "../components/AuthForgotPasswordForm";
import { initAuthForgotEmailForm } from "../components/AuthForgotEmailForm";
import { initAuthResetPasswordForm } from "../components/AuthResetPasswordForm";
import { CountrySelect } from "../components/CountrySelect";
import { PasswordToggleInput } from "../components/PasswordToggleInput";
import { DatePicker } from "../../components/DatePicker";
import { IntlPhoneInput } from "../components/IntlPhoneInput";
import { initFileUploaders } from "../../admin/components/FileUploader";
import { Form } from "../../core/Form";
import { Logger } from "../../utils/Logger";
import { Modal } from "../../utils/Modal";

export const AuthController = {
  login(): void {
    Logger.info("AuthController.login");
    initAuthLoginForm();
    const root = document.querySelector(".auth-page") ?? document;
    new PasswordToggleInput(root).init();
    new CountrySelect("#auth-country").init();
  },

  signup(): void {
    Logger.info("AuthController.signup");
    initAuthSignupForm();
    const root = document.querySelector(".auth-page") ?? document;
    new PasswordToggleInput(root).init();
    new CountrySelect("#auth-country").init();
    new DatePicker("#birth_date", { dateFormat: "Y-m-d", allowInput: true }).init();
  },

  forgotPassword(): void {
    Logger.info("AuthController.forgotPassword");
    initAuthForgotPasswordForm();
    const root = document.querySelector(".auth-page") ?? document;
    new PasswordToggleInput(root).init();
  },

  forgotEmail(): void {
    Logger.info("AuthController.forgotEmail");
    initAuthForgotEmailForm();
  },

  resetPassword(): void {
    Logger.info("AuthController.resetPassword");
    initAuthResetPasswordForm();
    const root = document.querySelector(".auth-page") ?? document;
    new PasswordToggleInput(root).init();
  },

  /** 비밀번호 변경 페이지: Form.ts로 제출 (validate 비밀번호 일치, onFail 에러 박스) */
  showChangePasswordForm(): void {
    Logger.info("AuthController.showChangePasswordForm");
    const root = document.querySelector(".profile-page") ?? document.querySelector(".auth-page") ?? document;
    new PasswordToggleInput(root).init();
    const form = document.getElementById("change-password-form") as HTMLFormElement | null;
    const errEl = document.getElementById("change-password-error") as HTMLElement | null;
    if (!form) return;

    const showError = (msg: string | null): void => {
      if (!errEl) return;
      if (msg == null || msg === "") {
        errEl.hidden = true;
        errEl.textContent = "";
        return;
      }
      errEl.textContent = msg;
      errEl.hidden = false;
    };

    new Form(form, {
      submitUrl: form.dataset.submitUrl ?? form.action,
      validate: (f) => {
        const newPassword = (f.querySelector<HTMLInputElement>('input[name="password"]')?.value ?? "").trim();
        const confirm = (f.querySelector<HTMLInputElement>('input[name="password_confirmation"]')?.value ?? "").trim();
        if (newPassword !== confirm) {
          return document.documentElement.lang?.startsWith("ko")
            ? "새 비밀번호와 확인이 일치하지 않습니다."
            : "New password and Confirm new password do not match.";
        }
        return null;
      },
      requestFailedMessage: document.documentElement.lang?.startsWith("ko") ? "요청에 실패했습니다." : "Request failed.",
      onSuccess: async (data) => {
        const msg = (data?.message ?? data?.msg ?? "") as string;
        const redirect = (data?.data as { redirect?: string } | undefined)?.redirect;
        if (msg) await Modal.alert(msg, document.documentElement.lang?.startsWith("ko") ? "비밀번호 변경" : "Change password");
        if (redirect) window.location.href = redirect;
      },
      onFail: (message, data) => {
        const errors = data?.errors as Record<string, string[]> | undefined;
        const first =
          errors?.current_password?.[0] ?? errors?.password?.[0] ?? errors?.password_confirmation?.[0] ?? message;
        showError(first ?? "");
      },
    }).init();

    form.addEventListener("submit", () => showError(null));
  },

  /** 이메일 변경: 1) 인증번호 발송 2) 인증번호 입력 후 확정 */
  showChangeEmailForm(): void {
    Logger.info("AuthController.showChangeEmailForm");
    const root = document.querySelector(".profile-page") ?? document.querySelector(".auth-page") ?? document;
    new PasswordToggleInput(root).init();
    const errEl = document.getElementById("change-email-error");
    const confirmErrEl = document.getElementById("change-email-confirm-error");
    const sendForm = document.getElementById("change-email-send-form") as HTMLFormElement | null;
    const confirmSection = document.getElementById("change-email-confirm-section");
    const confirmForm = document.getElementById("change-email-confirm-form") as HTMLFormElement | null;
    const codeInput = document.querySelector<HTMLInputElement>("[data-change-email-code]");
    const countdownEl = document.getElementById("change-email-countdown");
    let countdownTimerId: ReturnType<typeof setInterval> | null = null;

    const startCodeCountdown = (expiresAtIso: string): void => {
      if (countdownTimerId !== null) {
        clearInterval(countdownTimerId);
        countdownTimerId = null;
      }
      if (!countdownEl) return;
      const update = (): void => {
        const now = Date.now();
        const end = new Date(expiresAtIso).getTime();
        const secLeft = Math.max(0, Math.floor((end - now) / 1000));
        if (secLeft <= 0) {
          if (countdownTimerId !== null) clearInterval(countdownTimerId);
          countdownTimerId = null;
          countdownEl.textContent = document.documentElement.lang?.startsWith("ko") ? "만료됨" : "Expired";
          countdownEl.classList.add("is-expired");
          return;
        }
        const m = Math.floor(secLeft / 60);
        const s = secLeft % 60;
        countdownEl.textContent = `${m}:${s.toString().padStart(2, "0")}`;
        countdownEl.classList.remove("is-expired");
      };
      update();
      countdownTimerId = setInterval(update, 1000);
    };

    const showError = (el: HTMLElement | null, msg: string): void => {
      if (el) {
        el.textContent = msg;
        el.hidden = false;
      }
    };
    const hideError = (el: HTMLElement | null): void => {
      if (el) {
        el.hidden = true;
        el.textContent = "";
      }
    };

    if (sendForm) {
      sendForm.addEventListener("submit", () => hideError(errEl));
      new Form(sendForm, {
        submitUrl: sendForm.dataset.sendUrl ?? sendForm.action,
        loaderSelector: ".profile-page__btn-loader",
        requestFailedMessage: "Request failed.",
        onSuccess: async (data) => {
          const msg = (data?.message ?? data?.msg ?? "새 이메일로 인증번호를 발송했습니다. 아래에 인증번호를 입력해 주세요.") as string;
          await Modal.alert(msg, "이메일 변경");
          hideError(errEl);
          if (confirmSection) confirmSection.hidden = false;
          codeInput?.focus();
          const expiresAt = (data?.data?.expires_at ?? data?.expires_at) as string | undefined;
          if (expiresAt) startCodeCountdown(expiresAt);
        },
        onFail: (message, data) => {
          const errors = data?.errors as Record<string, string[]> | undefined;
          const first =
            errors?.current_password?.[0] ?? errors?.email?.[0] ?? message ?? "Please check your input.";
          showError(errEl, first);
        },
      }).init();
    }

    if (confirmForm && confirmErrEl) {
      confirmForm.addEventListener("submit", () => hideError(confirmErrEl));
      new Form(confirmForm, {
        submitUrl: confirmForm.dataset.confirmUrl ?? confirmForm.action,
        submitButtonSelector: "#change-email-confirm-btn",
        loaderSelector: "#change-email-confirm-btn .profile-page__btn-loader",
        requestFailedMessage: "Request failed.",
        onSuccess: (data) => {
          const redirect = (data?.data?.redirect ?? data?.redirect) as string | undefined;
          window.location.href = redirect ?? "/";
        },
        onFail: (message, data) => {
          const errors = data?.errors as Record<string, string[]> | undefined;
          const first = errors?.code?.[0] ?? message ?? "인증번호를 확인해 주세요.";
          showError(confirmErrEl, first);
        },
      }).init();
    }
  },

  /** PHP 액션명과 동기화 (GET 로그인 폼) */
  showLoginForm(): void {
    Logger.info("AuthController.showLoginForm");
    this.login();
  },
  /** PHP 액션명과 동기화 (GET 회원가입 폼) */
  showRegisterForm(): void {
    Logger.info("AuthController.showRegisterForm");
    this.signup();
  },
  /** PHP 액션명과 동기화 (GET 이메일 찾기 폼) */
  showForgotEmailForm(): void {
    Logger.info("AuthController.showForgotEmailForm");
    this.forgotEmail();
  },
  /** PHP 액션명과 동기화 (GET 비밀번호 찾기 폼) */
  showForgotPasswordForm(): void {
    Logger.info("AuthController.showForgotPasswordForm");
    this.forgotPassword();
  },
  /** PHP 액션명과 동기화 (GET 비밀번호 재설정 폼, 쿼리 ?token=) */
  showResetPasswordForm(): void {
    Logger.info("AuthController.showResetPasswordForm");
    this.resetPassword();
  },
  /** PHP 액션명과 동기화 (GET 비밀번호 재설정 폼, 경로 /reset-password/{token}) */
  showResetPasswordFormWithToken(): void {
    Logger.info("AuthController.showResetPasswordFormWithToken");
    this.resetPassword();
  },

  /** 프로필 기본정보 수정 폼: DatePicker, PhoneInput(래퍼), 기본정보 폼, 아바타 업로드 */
  showProfileForm(): void {
    Logger.info("AuthController.showProfileForm");
    new DatePicker("#profile-birth_date", { dateFormat: "Y-m-d", allowInput: true }).init();

    try {
      new IntlPhoneInput("#profile-phone-root", { initialCountry: "kr" }).init();
    } catch {
      // #profile-phone-root 없으면 스킵
    }

    const csrf =
      (document.querySelector('meta[name="csrf-token"]') as HTMLMetaElement | null)?.content ??
      (document.querySelector('input[name="_token"]') as HTMLInputElement | null)?.value ??
      "";

    // 기본정보 폼 (Form.ts)
    const form = document.getElementById("profile-edit-form") as HTMLFormElement | null;
    if (form) {
      new Form(form, {
        submitUrl: form.action,
        requestFailedMessage: document.documentElement.lang?.startsWith("ko") ? "요청에 실패했습니다." : "Request failed.",
        onSuccess: (data) => {
          const redirect = (data?.data as { redirect?: string } | undefined)?.redirect ?? (data?.redirect as string | undefined);
          if (redirect) {
            window.location.href = redirect;
            return;
          }
          const msg = (data?.message ?? data?.msg ?? "저장되었습니다.") as string;
          Modal.alert(msg);
        },
        onFail: (message) => {
          Modal.alert(message);
        },
      }).init();
    }

    initFileUploaders();
  },
};
